import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { ArrowUpRight, ArrowLeft, ArrowRight, ChevronRight, Play, Clock } from 'lucide-react';
import { api } from '../lib/api';
import SEOHead from '../components/common/SEOHead';
import NotFound from './NotFound';

const CALC_MAP = {
    affordability: { label: 'Affordability Calculator', url: '/affordability' },
    invest: { label: 'Investment View', url: '/invest' },
    match: { label: 'Match Assessment', url: '/match' },
};

const fmtDur = (s) => {
    if (!s) return '';
    return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
};

export default function BuyerCentreDetail() {
    const { slug } = useParams();
    const { data, isLoading, isError } = useQuery({
        queryKey: ['buyer-centre', slug],
        queryFn: async () => (await api.get(`/buyer-centre/${slug}`)).data,
        retry: false,
    });

    if (isLoading) return <div className="min-h-[60vh]" />;
    if (isError || !data) return <NotFound />;

    return (
        <div className="bg-white">
            <SEOHead
                title={`${data.name} — Buyer Centre · Southern Ontario`}
                description={data.kicker}
                path={`/buyer-centre/${slug}`}
                image={data.hero_image}
                type="article"
            />

            {/* ==================== HERO ==================== */}
            <section className="relative bg-brand-navy text-white">
                <img src={data.hero_image} alt={data.name} className="absolute inset-0 w-full h-full object-cover opacity-45" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 100%)' }} />
                <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 pt-28 pb-16 lg:pb-24">
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-white/70 font-mono-data">
                        <Link to="/buyer-centre" className="hover:text-white">Buyer Centre</Link>
                        <ChevronRight className="w-3 h-3" />
                        <span>{data.name}</span>
                    </div>
                    <h1
                        className="font-editorial mt-6 tracking-editorial max-w-4xl"
                        style={{ fontSize: 'clamp(40px, 6.4vw, 88px)', lineHeight: 1.03, letterSpacing: '-0.03em' }}
                        data-testid="bc-title"
                    >
                        {data.name}
                    </h1>
                    <p className="mt-5 font-editorial italic text-white/85 max-w-2xl" style={{ fontSize: '22px', lineHeight: 1.45 }}>
                        {data.kicker}
                    </p>
                </div>
            </section>

            {/* ==================== BODY ==================== */}
            <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-8 space-y-14">
                    {/* Intro */}
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        <p className="font-editorial text-black leading-relaxed" style={{ fontSize: '21px', letterSpacing: '-0.005em' }} data-testid="bc-intro">
                            {data.intro}
                        </p>
                    </motion.div>

                    {/* Steps */}
                    {data.steps?.length > 0 && (
                        <div data-testid="bc-steps">
                            <div className="overline">The playbook</div>
                            <h2 className="font-editorial mt-3 leading-tight" style={{ fontSize: '30px', letterSpacing: '-0.02em' }}>
                                {data.steps.length} steps, in order.
                            </h2>
                            <ol className="mt-8 border-t border-neutral-200">
                                {data.steps.map((s, i) => (
                                    <li key={i} className="border-b border-neutral-200 py-5 grid grid-cols-[3rem_1fr] gap-4">
                                        <div className="font-mono-data text-[13px] tabular text-neutral-400 pt-0.5">
                                            {String(i + 1).padStart(2, '0')}
                                        </div>
                                        <div className="text-[15.5px] leading-relaxed text-neutral-900">
                                            {s}
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}

                    {/* Tips */}
                    {data.tips?.length > 0 && (
                        <div className="bg-[#FAFAF8] border border-neutral-200 p-6 lg:p-8" data-testid="bc-tips">
                            <div className="overline">What most people miss</div>
                            <ul className="mt-4 space-y-3">
                                {data.tips.map((t, i) => (
                                    <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-neutral-800">
                                        <span className="text-[#2F6B47] mt-2 shrink-0" style={{ width: 6, height: 6, background: '#2F6B47' }} />
                                        <span>{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* FAQs */}
                    {data.faqs?.length > 0 && (
                        <div data-testid="bc-faqs">
                            <div className="overline">Answered honestly</div>
                            <h2 className="font-editorial mt-3 leading-tight" style={{ fontSize: '30px', letterSpacing: '-0.02em' }}>
                                Frequently asked.
                            </h2>
                            <div className="mt-8 border-t border-neutral-200">
                                {data.faqs.map((f, i) => <FaqRow key={i} q={f.q} a={f.a} />)}
                            </div>
                        </div>
                    )}

                    {/* Related videos */}
                    {data.related_videos_full?.length > 0 && (
                        <div data-testid="bc-related-videos">
                            <div className="overline">Watch these</div>
                            <h2 className="font-editorial mt-3 leading-tight" style={{ fontSize: '30px', letterSpacing: '-0.02em' }}>
                                Companion videos.
                            </h2>
                            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {data.related_videos_full.map((v) => (
                                    <Link key={v.slug} to={`/videos/${v.slug}`} className="group block relative overflow-hidden border border-neutral-200 hover:border-brand-navy transition-colors">
                                        <div className="relative aspect-video bg-neutral-200">
                                            <img src={v.thumbnail} alt={v.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                                            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.7) 100%)' }} />
                                            <Play className="absolute top-3 right-3 w-4 h-4 text-white/80" />
                                            {v.duration_sec && (
                                                <div className="absolute bottom-3 right-3 bg-black/70 text-white px-2 py-0.5 text-[10px] font-mono-data flex items-center gap-1">
                                                    <Clock className="w-2.5 h-2.5" /> {fmtDur(v.duration_sec)}
                                                </div>
                                            )}
                                            <div className="absolute bottom-3 left-3 font-editorial text-white text-[16px] leading-tight tracking-editorial line-clamp-2 pr-16">
                                                {v.title}
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <aside className="lg:col-span-4 space-y-10">
                    {data.related_calculators?.length > 0 && (
                        <div data-testid="bc-related-calcs">
                            <div className="overline">Run the numbers</div>
                            <ul className="mt-4">
                                {data.related_calculators.map((c) => {
                                    const meta = CALC_MAP[c];
                                    if (!meta) return null;
                                    return (
                                        <li key={c} className="border-b border-neutral-200 py-3">
                                            <Link to={meta.url} className="flex items-baseline justify-between gap-3 group">
                                                <span className="font-editorial text-[16px] leading-snug group-hover:italic">{meta.label}</span>
                                                <ArrowUpRight className="w-4 h-4 text-neutral-400 group-hover:text-black" />
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}

                    {data.related_reports?.length > 0 && (
                        <div data-testid="bc-related-reports">
                            <div className="overline">Related reports</div>
                            <ul className="mt-4">
                                {data.related_reports.map((r, i) => (
                                    <li key={i} className="border-b border-neutral-200 py-3">
                                        <Link to={r.url} className="flex items-baseline justify-between gap-3 group">
                                            <span className="font-editorial text-[16px] leading-snug group-hover:italic">{r.title}</span>
                                            <ArrowUpRight className="w-4 h-4 text-neutral-400 group-hover:text-black" />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Strategy Session CTA */}
                    <Link
                        to="/strategy-session"
                        data-testid="bc-strategy-cta"
                        className="block bg-brand-navy text-white p-6 hover:bg-neutral-800 transition-colors"
                       
                    >
                        <div className="text-[10px] tracking-[0.24em] uppercase text-white/60 font-mono-data">
                            Book a strategy session
                        </div>
                        <div className="font-editorial mt-3 leading-tight" style={{ fontSize: '22px' }}>
                            Thirty minutes. Independent. Free.
                        </div>
                        <div className="mt-4 flex items-center gap-2 text-[12.5px]">
                            Book a session <ArrowUpRight className="w-4 h-4" />
                        </div>
                    </Link>
                </aside>
            </section>

            {/* ==================== PREV / NEXT ==================== */}
            <section className="border-t border-neutral-200 bg-[#FAFAF8]">
                <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10 flex items-center justify-between gap-4">
                    {data.prev_slug ? (
                        <Link to={`/buyer-centre/${data.prev_slug}`} className="flex items-center gap-3 text-[13.5px] hover:underline">
                            <ArrowLeft className="w-4 h-4" /> Previous
                        </Link>
                    ) : <span />}
                    <Link to="/buyer-centre" className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data hover:text-black">
                        All 12 pillars
                    </Link>
                    {data.next_slug ? (
                        <Link to={`/buyer-centre/${data.next_slug}`} className="flex items-center gap-3 text-[13.5px] hover:underline">
                            Next <ArrowRight className="w-4 h-4" />
                        </Link>
                    ) : <span />}
                </div>
            </section>
        </div>
    );
}


function FaqRow({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-neutral-200">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-start justify-between gap-4 py-4 text-left"
            >
                <span className="font-editorial text-[18px] leading-snug tracking-editorial">{q}</span>
                <ChevronRight className={`w-5 h-5 mt-1 text-neutral-400 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
            </button>
            {open && (
                <div className="pb-5 text-[14.5px] text-neutral-700 leading-relaxed">
                    {a}
                </div>
            )}
        </div>
    );
}
